import React from "react";
import { Add } from "@mui/icons-material";
import { Box, Button, ButtonBase, Tooltip, Typography } from "@mui/material";
import { colors } from "../../../ui/colors";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import AddCardV from "./AddCardV";
import { useVaultLogic } from "../../hooks/useVaultLogic";
import VaultSearch from "./VaultSearch";
import VaultList from "./VaultList";

type Props = {
  unlocked: boolean;
  companyId: number;
  remainingSeconds: number;
  isPreviewMode?: boolean;
};

const SideVault: React.FC<Props> = ({ unlocked, companyId, remainingSeconds, isPreviewMode }) => {
  const { search, setSearch, filteredCards, handleAddSecret, handleUpdateSecret, handleDeleteSecret, isVerified, handleVerify, buildSecretDetail } =
    useVaultLogic(unlocked, { isPreviewMode });

  const addModalKey = "add-secret";

  return (
    <Box
      sx={{
        width: 280,
        background: colors.lightGray.base,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        maxHeight: "100%",
        p: 2,
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <VaultSearch search={search} onSearchChange={setSearch} disabled={!unlocked} />

        <Typography sx={{ mt: 4, mb: 1, fontSize: "small" }}>MY SECRETS</Typography>

        <VaultList
          filteredCards={filteredCards}
          unlocked={unlocked}
          search={search}
          isVerified={isVerified}
          handleVerify={handleVerify}
          buildSecretDetail={buildSecretDetail}
          handleDeleteSecret={handleDeleteSecret}
          handleUpdateSecret={handleUpdateSecret}
          remainingSeconds={remainingSeconds}
          isPreviewMode={isPreviewMode}
        />
      </Box>

      <Box sx={{ width: "100%" }}>
        <BaseModalComponent
          modalKey={addModalKey}
          elementTrigger={
            <Tooltip
              arrow
              placement="top"
              PopperProps={{
                sx: { zIndex: 999999 },
              }}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: "0.8rem",
                    backgroundColor: colors.orange.base,
                    color: "white",
                    padding: "15px 12px",
                    borderRadius: 3,
                  },
                },
                arrow: {
                  sx: {
                    color: colors.orange.base,
                  },
                },
              }}
              title="To add a new secret, first unlock your vault. If it’s already unlocked, wait for it to unlock automatically or refresh the page."
              disableHoverListener={unlocked}
            >
              <div>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={!unlocked || isPreviewMode}
                  startIcon={<Add />}
                  sx={{
                    borderStyle: "dashed",
                    borderRadius: 3,
                    color: !unlocked || isPreviewMode ? "grey.400" : colors.orange.base,
                    borderColor: !unlocked || isPreviewMode ? "grey.400" : colors.orange.base,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": {
                      borderStyle: "dashed",
                      borderColor: !unlocked || isPreviewMode ? "grey.400" : colors.orange.base,
                    },
                  }}
                >
                  Add New Secret
                </Button>
              </div>
            </Tooltip>
          }
        >
          <AddCardV modalKey={addModalKey} userCompanyId={companyId} onSave={handleAddSecret} remainingSeconds={remainingSeconds} />
        </BaseModalComponent>
      </Box>
    </Box>
  );
};

export default SideVault;
